import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './FollowStrategyManager.css';
import { CsrfTokenContext } from '../CsrfTokenContext'; // Assuming you have this context set up
import Switch from 'react-switch';
import DecisionModal from './DecisionModal';
import { setOriginalStrategies } from '../pages/slices/mySpaceSlicer';
import { isTokenExpired, handleLogin } from '../pages/utils/AuthenticationHelper';
import TextBaloon from './TextBaloon'; 

const FollowStrategyManager = () => {
  const [status, setStatus] = useState(null);
  const [strategies, setStrategies] = useState([]);  
  // const [originalStrategies, setOriginalStrategies] = useState([]);  
  const [changedStrategies, setChangedStrategies] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);  
  const [canDelete, setCanDelete] = useState(false);  
  const [selectedFollowId, setSelectedFollowId] = useState(0);  
  const [modalShow, setModalShow] = useState(false);
  const [actionStatus, setActionStatus] = useState(false);        
  const [spin, setSpin] = useState(false);        
  const [selectedStrategy, setSelectedStrategy] = useState(0);        

  const { walletId } = useSelector(state => state.globalData);
  const { symbolIcons } = useSelector(state => state.streamCryptoInfo);
  const { originalStrategies } = useSelector(state => state.mySpace);

  const handleShow = () => setModalShow(true);
  const handleClose = () => {
    setModalShow(false);
    setSelectedFollowId(0);
    setCanDelete(false);
  };

  const csrfToken = useContext(CsrfTokenContext); 
 
  const dispatch = useDispatch();
  
  const refreshTokenIfNeeded = useCallback(async () => {
    if (isTokenExpired()) {        
      await handleLogin();
    }    
  },[]);

  const handleCheckChange = (index) => {
    let newChangedStrategies = [...changedStrategies];

    const updatedStrategies = strategies.map((strategy, i) => {
      if (i === index) {
        const updatedStrategy = { ...strategy, following: !strategy.following };

        // Check if the updated strategy differs from the original
        if (updatedStrategy.following !== originalStrategies[i].following) {
          // Add or update the changed strategy in the list
          if (!newChangedStrategies.some(item => item.follow_id === updatedStrategy.follow_id)) {
            newChangedStrategies.push(updatedStrategy);
          }
        } else {
          // Remove the strategy from the list if it matches the original
          newChangedStrategies = newChangedStrategies.filter(item => item.follow_id !== updatedStrategy.follow_id);
        }

        return updatedStrategy;
      }
      return strategy;
    });

    setStrategies(updatedStrategies);
    setChangedStrategies(newChangedStrategies);
    setIsButtonDisabled(newChangedStrategies.length === 0);
    setStatus('');
};
  
  const updFollowStrategy = async (strategy_set) => {
    try {       
      
        const filteredStrategySet = strategy_set.map(({ follow_id, following, note }) => ({
          follow_id,
          following,
          note
        }));
        
        await refreshTokenIfNeeded();
                      
        const response = await fetch(`/api/upd_follow_strategy`, {
            method: 'POST',
            // credentials: 'include',
            headers: {              
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken, // Ensure the CSRF token is correctly passed
            },
            body: JSON.stringify({walletId : walletId, strategy_set : filteredStrategySet})
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error);
        }
        setStatus('Saved!');

        getAllFollowStrategies();       
  
    } catch (err) {
      console.error('Error fetching data:', err);     
    }
};

const del_strategy = async (wallet, followId) => {
  try {
    await refreshTokenIfNeeded();
      setActionStatus(false);
      setSpin(true);
      
      const response = await fetch(`/api/del_follow_strategy`, {
          method: 'DELETE',
          // credentials: 'include',
          headers: {            
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken // Ensure csrfToken is correctly set
          },
          body: JSON.stringify({
              wallet: wallet,
              follow_id: followId
          })
      });

      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error);
      }

      setActionStatus(true);
      setSpin(false);
      setCanDelete(false);

      setTimeout(() => {
          setModalShow(false);
          setActionStatus(false);
          setStatus('Strategy follow has been deleted!');
          getAllFollowStrategies();
      }, 1000);

  } catch (err) {
      console.error('Error deleting strategy:', err);
      setModalShow(false);
      setActionStatus(false);
      setSpin(false);
  }
};

const saveChanges = () => {
  // Save only changed items logic here  
  const updatedStrategies = strategies.map((strategy, i) => {
    const noteElement = document.getElementById(i);
  
    if (noteElement) {
      return {
        ...strategy, // Spread the existing strategy properties
        note: noteElement.value.trim(), // Update the note
      };
    }
    return strategy; // Return the original strategy if no note element
  });
  
  updFollowStrategy(updatedStrategies);  
  
};

const deleteStrategy = () =>{
  del_strategy(walletId, selectedFollowId);
} 

const handleSelectedRow = (index, strategy) => {

  setSelectedFollowId(index);
  setSelectedStrategy(strategy);
  setCanDelete(true);
  setStatus('');

};

const getAllFollowStrategies = useCallback(async () => {
  try {
    await refreshTokenIfNeeded();
              
    const response = await fetch(`/api/getAllFollowStrategies?walletId=${walletId}`, {
      method: 'GET',
      // credentials: 'include',
      headers: {        
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error);
    }
    const result = await response.json();
   
    setStrategies(result);
    dispatch(setOriginalStrategies(result));

  } catch (err) {
    console.error('Error fetching data:', err);
    setStatus('An error occurred: ' + err);
  }
}, [csrfToken, walletId, dispatch, refreshTokenIfNeeded]);

  
  useEffect(() => { 
    
    getAllFollowStrategies();

  }, [getAllFollowStrategies]);

  const cellStyle = {
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
 
  const handleInputChange = (index, value) => {
    // Update the strategies array
    setStrategies((prevStrategies) =>
      prevStrategies.map((strategy, i) =>
        i === index ? { ...strategy, note: value } : strategy
      )
    );

    // Enable or disable the button based on input
    setIsButtonDisabled(!value.trim());
  };


  return (
    <>      
        <div className="contentWrapper" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', width:'100%' }}>
        <br/>
        <TextBaloon textColor='#d2d2d2' text={<>Once you start following strategies, they’ll be listed here. You can easily turn them on or off, or delete them if you feel the market conditions (like a bear market) aren’t ideal. Keep in mind, signals don’t happen every day, so it’s normal not to receive email notifications for a while. <br/> Feel free to add a short note in the “Note” field to help remind yourself what the selected strategy is about, for example, “bear market,” “overbought signal,” or anything else that makes it easier to identify.
                                </>} color='#5358b7' />
          <div style={{fontSize:'25px', margin:'20px'}}>Your Strategy Watchlist</div>
          <div style={{ height: '100px', paddingTop: '15px', flex: '1' }}>
            <div className="tableContainer">
              <table className="table">
                <thead>
                  <tr style={{ backgroundColor: '#8cb753', color: '#3a4b00' }}>
                    <th></th>
                    <th style={{ textAlign: 'left' }}>Symbol</th>
                    <th>Interval</th>
                    <th>Ema 1</th>
                    <th>Ema 2</th>
                    <th>Strategy</th>
                    <th>Origin</th>
                    <th>Note</th>
                    <th style={{ zIndex: '1' }}>Following</th>
                  </tr>
                </thead>
                <tbody>
                  {strategies.length > 0 && strategies.map((strategy, index) => (
                    <tr key={index} onClick={() => handleSelectedRow(strategy.follow_id, strategy.strategy)} className={strategy.follow_id === selectedFollowId ? 'selectedRow' : ''}>
                      <td>
                        <img src={symbolIcons[strategy.symbol.replace('-', '') + 'T']} alt="" style={{ width: '30px', borderRadius: '50%' }} />
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'left', whiteSpace:'nowrap' }}>
                        {strategy.symbol}
                      </td>
                      <td style={{ ...cellStyle }}>{strategy.interval}</td>
                      <td style={{ ...cellStyle }}>{strategy.ema1}</td>
                      <td style={{ ...cellStyle }}>{strategy.ema2}</td>
                      <td style={{ ...cellStyle }}>{strategy.strategy}</td>
                      <td style={{ ...cellStyle, color: strategy.is_default_strategy ? '#72920f' : '#2962ff' }}>{strategy.is_default_strategy ? 'System' : 'Custom'}</td>                      
                      <td style={{ ...cellStyle }}><input id={index} type='text' className='noteText' value={strategy.note || ''} onChange={(e) => handleInputChange(index, e.target.value)} placeholder='enter a short note' title='OPTIONAL'></input></td>
                      <td style={{ ...cellStyle }}>
                        <Switch
                          onChange={() => handleCheckChange(index)}
                          checked={strategy.following}
                          offColor="#888"
                          onColor="#00c100"
                          offHandleColor="#fff"
                          onHandleColor="#fff"
                        />
                      </td>
                    </tr>
                  ))}
                  {strategies.length === 0 &&
                    <tr>
                      <td colSpan={9} style={{ width: '10%', fontSize: '15pt' }}>No data yet, your followed strategies will be listed here.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
          <div className="button_wrapper_follow" style={{ marginTop: 'auto' }}>
            <div
              className={`button_container_fw button_save ${isButtonDisabled ? 'disable_button_follow' : ''}`}
              onClick={!isButtonDisabled ? saveChanges : undefined} >
              <i className="bi bi-save-fill" style={{ marginRight: '5px' }}></i>
              Save Changes
            </div>
            <div
              className={`button_container_fw button_delete ${!canDelete ? 'disable_button_follow' : ''}`}
              onClick={canDelete ? handleShow : undefined} >
              <i className="bi bi-trash-fill" style={{ marginRight: '5px' }}></i>
              Remove Follow Strategy
            </div>  
          </div>
          {status && <p>{status}</p>}
        </div>     
      <DecisionModal
        show={modalShow}
        onHide={handleClose}
        onConfirm={deleteStrategy}
        title={`You will no longer follow the Strategy ${selectedStrategy}`}
        spin={spin}
        actionConcluded={actionStatus}
      />
    </>
  );
};

export default FollowStrategyManager;